import { useId, useLayoutEffect } from 'react';
import { create } from 'zustand';

/**
 * IS A TOUR STEP ON SCREEN? The one answer, for anything that must keep out
 * of the tour's way (today: the toasts, app/toastConfig.ts).
 *
 * Each tour or coach-mark surface says so itself while it is VISIBLE, with
 * useTourStepPresence(): the onboarding speech bubble and spotlight, the
 * guided tool hints (including their pulse-only stages, where the bubble is
 * hidden and the pulsing target is the step), the circles intro and the
 * camera coach mark. Reading the onboarding store's flags instead would be a
 * second definition of "visible" that drifts: `isActive` is true on screens
 * where nothing is drawn, and the coach mark is not in that store at all.
 *
 * A step also says which edge an alert may use without covering it: 'bottom'
 * for every step whose bubble and target are in the top part of the screen or
 * above the bottom nav (the default), 'top' for one that sits at the very
 * bottom (the camera coach mark, over the session's camera button; the Giraffe
 * guided tool inside its transform dialog, whose pulsing controls are in the
 * dialog's bottom bar). Any 'top' wins: a bottom alert would cover that step.
 */
export type TourAlertEdge = 'top' | 'bottom';

interface TourPresenceState {
  /** The visible steps, by a key each surface owns, with the edge its alerts may use. */
  steps: Record<string, TourAlertEdge>;
  show: (key: string, alertEdge: TourAlertEdge) => void;
  hide: (key: string) => void;
}

export const useTourPresenceStore = create<TourPresenceState>((set) => ({
  steps: {},
  show: (key, alertEdge) =>
    set((s) => (s.steps[key] === alertEdge ? s : { steps: { ...s.steps, [key]: alertEdge } })),
  hide: (key) =>
    set((s) => {
      if (!(key in s.steps)) return s;
      const rest = { ...s.steps };
      delete rest[key];
      return { steps: rest };
    }),
}));

/** True while any tour or coach-mark step is on screen. */
export function isTourStepVisible(state: Pick<TourPresenceState, 'steps'>): boolean {
  return Object.keys(state.steps).length > 0;
}

/** The edge an alert may use while a step is on screen; null when none is. */
export function tourAlertEdge(state: Pick<TourPresenceState, 'steps'>): TourAlertEdge | null {
  const edges = Object.values(state.steps);
  if (edges.length === 0) return null;
  return edges.includes('top') ? 'top' : 'bottom';
}

/**
 * Marks the calling surface's step as on screen while `visible`. A layout
 * effect, so the mark is in place before any effect of the same commit can
 * fire a toast.
 *
 * Two effects, so a change of edge alone is ONE `show` on the existing key
 * (b-172): one effect holding both would run hide then show, and the toast
 * guard would read that as the tour ending and starting again.
 */
export function useTourStepPresence(visible: boolean, alertEdge: TourAlertEdge = 'bottom'): void {
  const key = useId();
  useLayoutEffect(() => {
    if (!visible) return;
    return () => useTourPresenceStore.getState().hide(key);
  }, [key, visible]);
  useLayoutEffect(() => {
    if (visible) useTourPresenceStore.getState().show(key, alertEdge);
  }, [key, visible, alertEdge]);
}
